import { Fragment } from "react";
import { ArrowRight, Mail } from "lucide-react";

const BuildingIcon = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="currentColor" aria-hidden="true">
    <path d="M26 6 44 2v54H26z" />
    <path d="M10 18h14v38H10z" />
    <path d="M44 22h10v34H44z" />
    <g fill="#fff">
      <rect x="31" y="10" width="4" height="5" rx="0.8" />
      <rect x="38" y="10" width="4" height="5" rx="0.8" />
      <rect x="31" y="20" width="4" height="5" rx="0.8" />
      <rect x="38" y="20" width="4" height="5" rx="0.8" />
      <rect x="31" y="30" width="4" height="5" rx="0.8" />
      <rect x="38" y="30" width="4" height="5" rx="0.8" />
      <rect x="32" y="42" width="9" height="14" rx="0.8" />
      <rect x="14" y="24" width="4" height="5" rx="0.8" />
      <rect x="14" y="34" width="4" height="5" rx="0.8" />
      <rect x="14" y="44" width="4" height="5" rx="0.8" />
      <rect x="47" y="28" width="4" height="5" rx="0.8" />
      <rect x="47" y="38" width="4" height="5" rx="0.8" />
      <rect x="47" y="48" width="4" height="5" rx="0.8" />
    </g>
  </svg>
);

const PersonIcon = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="currentColor" aria-hidden="true">
    <circle cx="32" cy="18" r="13" />
    <path d="M32 35c-11 0-20 7.6-20 17a2 2 0 0 0 2 2h36a2 2 0 0 0 2-2c0-9.4-9-17-20-17Z" />
  </svg>
);

const GroupIcon = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="currentColor" aria-hidden="true">
    <circle cx="14" cy="19" r="8" />
    <path d="M14 29c-6.5 0-12 4.4-12 10.2V44h24v-4.8C26 33.4 20.5 29 14 29Z" />
    <circle cx="50" cy="19" r="8" />
    <path d="M50 29c-6.5 0-12 4.4-12 10.2V44h24v-4.8C62 33.4 56.5 29 50 29Z" />
    <circle cx="32" cy="16" r="11" />
    <path d="M32 30c-8.8 0-16 5.9-16 13.6V50h32v-6.4C48 35.9 40.8 30 32 30Z" />
  </svg>
);

const STEPS = [
  {
    key: "company",
    icon: BuildingIcon,
    title: "VENDOR/SUPPLIER",
    caption: "Your Organization",
    accent: "text-[#1b3a8f]",
    iconColor: "text-[#1b3a8f]",
    ring: "border-[#1b3a8f]",
    card: "bg-[#f5f8ff] border-[#dbe5ff]",
    headline: "Vendor/Supplier logs in",
    body: "to ConfirmTerms",
  },
  {
    key: "customer",
    icon: PersonIcon,
    title: "CUSTOMER",
    caption: "Credit Applicant",
    accent: "text-[#2f8a2f]",
    iconColor: "text-[#2f8a2f]",
    ring: "border-[#2f8a2f]",
    card: "bg-[#f5fbf4] border-[#d6ead4]",
    headline: "Customer receives email",
    body: "with secure link to start credit application",
  },
  {
    key: "reference",
    icon: GroupIcon,
    title: "REFERENCE",
    caption: "Trade Reference",
    accent: "text-[#6b34d4]",
    iconColor: "text-[#6b34d4]",
    ring: "border-[#6b34d4]",
    card: "bg-[#f8f5ff] border-[#e4d9ff]",
    headline: "Reference receives email",
    body: "requesting a trade reference on the customer",
  },
];

export const HowItWorks = () => (
  <section className="bg-white py-16 lg:py-24" data-testid="how-it-works">
    <div className="mx-auto max-w-6xl px-6">
      <div className="text-center">
        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-600">
          How ConfirmTerms works
        </div>
        <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
          Three steps to verified credit
        </h2>
      </div>

      <div className="mt-14 grid items-stretch gap-x-6 gap-y-12 lg:grid-cols-[1fr_auto_1fr_auto_1fr]">
        {STEPS.map((s, i) => (
          <Fragment key={s.key}>
            <div
              className="ct-rise flex h-full flex-col items-center text-center"
              style={{ animationDelay: `${i * 120}ms` }}
              data-testid={`hiw-step-${s.key}`}
            >
              <s.icon className={`h-16 w-16 ${s.iconColor}`} />
              <h3
                className={`mt-5 font-display text-2xl font-black tracking-tight sm:text-3xl ${s.accent}`}
              >
                {s.title}
              </h3>
              <p className="mt-2 text-base text-slate-600">{s.caption}</p>

              <div
                className={`mt-8 flex w-full flex-1 items-center gap-4 rounded-2xl border p-5 text-left ${s.card}`}
              >
                <div
                  className={`grid h-12 w-12 shrink-0 place-items-center rounded-full border-2 bg-white ${s.ring}`}
                >
                  <Mail className={`h-5 w-5 ${s.iconColor}`} strokeWidth={2} />
                </div>
                <div>
                  <div className={`font-display text-base font-bold ${s.accent}`}>{s.headline}</div>
                  <div className="mt-0.5 text-sm leading-snug text-slate-600">{s.body}</div>
                </div>
              </div>
            </div>

            {i < STEPS.length - 1 && (
              <div className="hidden self-start pt-[3.2rem] lg:block" aria-hidden="true">
                <ArrowRight className="h-7 w-7 text-slate-300" strokeWidth={2.4} />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  </section>
);
